import { Avatar } from "@dash/avatar";
import { css, useTheme } from "@emotion/react";
import { useProfiles } from "@megarax/features";
import { useMotionValue, useSpring, useTransform } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { TicketListItemDto } from "@megaron/support-contracts";

import { SupportTicketAssigneesTooltip } from "../shared/SupportTicketAssigneesTooltip";
import { AssigneeRow, buildAssigneeRow } from "../shared/supportTicketAssigneeRow";

export function AssigneesCell({ item }: { item: TicketListItemDto }) {
  const theme = useTheme();
  const { profiles } = useProfiles();
  const assignees = item.assignees;
  const [hoveredEmail, setHoveredEmail] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  const anchorRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const animationFrameRef = useRef<number | null>(null);

  const springConfig = { stiffness: 100, damping: 15 };
  const x = useMotionValue(0);
  const rotate = useSpring(useTransform(x, [-100, 100], [-45, 45]), springConfig);
  const translateX = useSpring(useTransform(x, [-100, 100], [-50, 50]), springConfig);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hoveredEmail) return undefined;
    const sync = () => {
      const el = anchorRefs.current.get(hoveredEmail);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setCoords({ x: rect.left + rect.width / 2, y: rect.top });
    };
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [hoveredEmail]);

  if (!assignees?.length) {
    return <div css={{ fontSize: "0.875rem", color: theme.colors.secondaryText }}>–</div>;
  }

  const emails = assignees.map((a) => a.email);
  const foundProfiles = profiles(emails) ?? [];
  const profileByEmail = new Map(foundProfiles.map((p) => [p.email, p]));

  const rows: AssigneeRow[] = assignees.map((assignee) =>
    buildAssigneeRow(profileByEmail.get(assignee.email), assignee.email),
  );
  const visibleRows = rows.slice(0, 3);

  const syncCoords = (email: string) => {
    const el = anchorRefs.current.get(email);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ x: rect.left + rect.width / 2, y: rect.top });
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    const node = event.currentTarget;
    const halfWidth = node.offsetWidth / 2;
    const pointerOffset = event.nativeEvent.offsetX;
    animationFrameRef.current = requestAnimationFrame(() => {
      x.set(pointerOffset - halfWidth);
    });
  };

  const handleMouseEnter = (email: string) => {
    setHoveredEmail(email);
    syncCoords(email);
    requestAnimationFrame(() => syncCoords(email));
  };

  const handleMouseLeave = () => {
    setHoveredEmail(null);
    x.set(0);
  };

  const registerAnchor =
    (email: string) =>
    (element: HTMLDivElement | null): void => {
      if (element) {
        anchorRefs.current.set(email, element);
      } else {
        anchorRefs.current.delete(email);
      }
    };

  const hoveredRow = rows.find((row) => row.email === hoveredEmail);

  const avatarOverlap = css({
    position: "relative",
    borderRadius: "50%",
    border: `2px solid ${theme.colors.background}`,
    transition: "transform 0.5s ease",
    ":hover": { transform: "scale(1.05)" },
  });

  return (
    <>
      <div css={{ display: "flex", alignItems: "center" }}>
        {visibleRows.map((row, index) => (
          <div
            key={row.email}
            ref={registerAnchor(row.email)}
            css={{
              marginLeft: index > 0 ? "-8px" : 0,
              zIndex: index,
              flexShrink: 0,
              "&:hover": { zIndex: 40 },
            }}
            onMouseEnter={() => handleMouseEnter(row.email)}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
          >
            <div css={avatarOverlap}>
              <Avatar src={row.pictureUrl} size="30px" />
            </div>
          </div>
        ))}
      </div>
      <SupportTicketAssigneesTooltip
        mounted={mounted}
        hoveredEmail={hoveredEmail}
        coords={coords}
        hoveredRow={hoveredRow}
        translateX={translateX}
        rotate={rotate}
      />
    </>
  );
}
